import { derived, get, writable } from "svelte/store";
import { appManager } from "../AppManager";
import { SearchControlManager } from "../components/search/SearchControlManager";
import { LayersControlManager } from "./layers/LayersControlManager";
import { MapManager } from "../components/map/MapManager";
import { ModelViewMapManager } from "./map/ModelViewMapManager";
import { ModelInfoManager } from "./info/ModelInfoManager";
import UrlParameter from "../UrlParameter";
import * as turf from "@turf/turf";
import { OriginDestinationLocationType } from "../api/projects/data/od/OriginDestinationLocationType";
import { OriginDestinationMatrixType } from "../api/projects/data/od/OriginDestinationMatrixType";
import { Guid } from "guid-typescript";
import { ModelLocationInfoManager } from "./location_info/ModelLocationInfoManager";
import { ModelSegmentInfoManager } from "./segment_info/ModelSegmentInfoManager";
export class ModelViewManager {
    constructor() {
        this.modelGlobalIdStore = writable(undefined);
        this.modelTranslationKeyStore = writable(undefined);
        this.layersAndStatusStore = writable([]);
        this.showCyclingScoreStore = writable(true);
        this.showRelevantLocationsStore = writable(true);
        this.selectionStore = writable({});
        this.tripsFilterStore = writable(undefined);
        this.locationsFilterStore = writable(undefined);
        this.alertStateStore = writable({ open: false });
        this.canCreateProjectStore = writable(false);
        this.modelDataStore = writable(undefined);
        this.showRelevantLocationsToggleStore = writable(true);
        this.selectedLocationStore = writable(undefined);
        this.selectedSegmentStore = writable(undefined);
        this.layersControlManager = new LayersControlManager(this);
        this.modelViewMapManager = new ModelViewMapManager(this);
        this.mapManager = new MapManager(this);
        this.searchControlManager = new SearchControlManager();
        this.modelInfoManager = new ModelInfoManager(this);
        this.modelLocationInfoManager = new ModelLocationInfoManager(this);
        this.modelSegmentInfoManager = new ModelSegmentInfoManager(this);
        this.modelGlobalIdStore.subscribe((modelGlobalId) => {
            if (typeof modelGlobalId === "undefined")
                return;
            this.getModelDetails();
        });
        this.searchControlManager.getSelectedResultStore().subscribe(result => {
            if (typeof result === "undefined")
                return;
            this.modelViewMapManager.zoomTo({ center: result.location, zoom: 14 });
        });
    }
    getModelDataStore() {
        return this.modelDataStore;
    }
    getCanCreateProjectStore() {
        return this.canCreateProjectStore;
    }
    getAlertStateStore() {
        return this.alertStateStore;
    }
    getSelectionStore() {
        return this.selectionStore;
    }
    getSelectedLocationStore() {
        return this.selectedLocationStore;
    }
    getSelectedSegmentStore() {
        return this.selectedSegmentStore;
    }
    getTripsFilterStore() {
        return this.tripsFilterStore;
    }
    getLocationsFilterStore() {
        return this.locationsFilterStore;
    }
    getShowCyclingScoreStore() {
        return this.showCyclingScoreStore;
    }
    getShowRelevantLocationsStore() {
        return this.showRelevantLocationsStore;
    }
    getShowRelevantLocationsToggleStore() {
        return this.showRelevantLocationsToggleStore;
    }
    getModelInfoManager() {
        return this.modelInfoManager;
    }
    getModelLocationInfoManager() {
        return this.modelLocationInfoManager;
    }
    getModelSegmentInfoManager() {
        return this.modelSegmentInfoManager;
    }
    getModelGlobalId() {
        return get(this.modelGlobalIdStore);
    }
    getLayersAndStatusStore() {
        return this.layersAndStatusStore;
    }
    getLayersControlManager() {
        return this.layersControlManager;
    }
    getSearchControlManager() {
        return this.searchControlManager;
    }
    getModelTranslationKeyStore() {
        return this.modelTranslationKeyStore;
    }
    getMapManager() {
        return this.mapManager;
    }
    initialize(modelGlobalId) {
        this.modelGlobalIdStore.set(modelGlobalId);
        const segmentIdString = get(UrlParameter.Get("segment", undefined));
        if (typeof segmentIdString !== "undefined") {
            const segmentId = Number(segmentIdString);
            if (typeof segmentId !== "undefined") {
                this.selectionStore.set({ segmentId: segmentId });
            }
        }
        const locationIdString = get(UrlParameter.Get("location", undefined));
        if (typeof locationIdString !== "undefined") {
            const locationId = Number(locationIdString);
            if (typeof locationId !== "undefined") {
                this.selectionStore.set({ locationId: locationId });
            }
        }
        this.selectionStore.subscribe(selection => {
            if (typeof selection === "undefined" ||
                (typeof selection.locationId === "undefined" &&
                    typeof selection.segmentId === "undefined")) {
                UrlParameter.Get("location", undefined).set(undefined);
                UrlParameter.Get("segment", undefined).set(undefined);
                this.selectedLocationStore.set(undefined);
                this.selectedSegmentStore.set(undefined);
                this.tripsFilterStore.set(undefined);
                this.locationsFilterStore.set(undefined);
                if (typeof this.modelViewMapManager.map !== "undefined") {
                    if (this.modelViewMapManager.map.getZoom() < 14) {
                        this.canCreateProjectStore.set(false);
                    }
                }
            }
            else if (typeof selection.locationId !== "undefined") {
                UrlParameter.Get("location", undefined).set(selection.locationId.toString());
                this.onLocationSelected(selection.locationId);
            }
            else {
                UrlParameter.Get("segment", undefined).set(selection.segmentId.toString());
                this.onSegmentSelected(selection.segmentId);
            }
        });
    }
    onMapCreated(map) {
        this.modelViewMapManager.initialize(map);
    }
    onLocationSelected(locationId) {
        appManager.api.trips.getModelLocation({ id: locationId, modelGlobalId: get(this.modelGlobalIdStore) }).then(locationData => {
            const tripsPerLayer = [];
            locationData.startingTrips.forEach(x => {
                let trips = tripsPerLayer.find(y => y.layerId == x.layerId);
                if (typeof trips === "undefined") {
                    trips = { layerId: x.layerId, trips: [] };
                    tripsPerLayer.push(trips);
                }
                trips.trips.push(...x.trips.map(x => x.id));
            });
            locationData.endingTrips.forEach(x => {
                let trips = tripsPerLayer.find(y => y.layerId == x.layerId);
                if (typeof trips === "undefined") {
                    trips = { layerId: x.layerId, trips: [] };
                    tripsPerLayer.push(trips);
                }
                trips.trips.push(...x.trips.map(x => x.id));
            });
            const selection = get(this.selectionStore);
            if (selection.locationId !== locationId)
                return;
            this.tripsFilterStore.set(tripsPerLayer);
            this.locationsFilterStore.set([selection.locationId]);
            this.selectedLocationStore.set(locationData);
            this.selectedSegmentStore.set(undefined);
            console.log(tripsPerLayer);
            this.modelViewMapManager.zoomTo({ center: { lng: locationData.longitude, lat: locationData.latitude }, zoom: 15 }, false);
            this.canCreateProjectStore.set(true);
        });
    }
    onMapViewOrDataChanged() {
        const selection = get(this.selectionStore);
        if (typeof selection !== "undefined" &&
            (typeof selection.locationId !== "undefined" ||
                typeof selection.segmentId !== "undefined")) {
            return;
        }
        if (this.modelViewMapManager.map.getZoom() < 12) {
            this.canCreateProjectStore.set(false);
            return;
        }
        const locations = this.modelViewMapManager.getLocationsInView();
        if (locations.length === 0 || locations.length > 25) {
            this.canCreateProjectStore.set(false);
            return;
        }
        this.canCreateProjectStore.set(true);
    }
    onSegmentSelected(segmentIdDirection) {
        const forward = segmentIdDirection > 0;
        let segmentId = segmentIdDirection;
        if (segmentIdDirection < 0)
            segmentId = -segmentIdDirection;
        appManager.api.trips.getModelSegment({ id: segmentId, modelGlobalId: get(this.modelGlobalIdStore) }).then(segmentData => {
            const tripsPerLayer = [];
            const locations = [];
            if (forward) {
                segmentData.forwardTrips.forEach(ts => {
                    locations.push(...ts.startLocations.map(x => x.id));
                    locations.push(...ts.endLocations.map(x => x.id));
                    tripsPerLayer.push(ts);
                });
            }
            else {
                segmentData.backwardTrips.forEach(ts => {
                    locations.push(...ts.startLocations.map(x => x.id));
                    locations.push(...ts.endLocations.map(x => x.id));
                    tripsPerLayer.push(ts);
                });
            }
            const selection = get(this.selectionStore);
            if (selection.segmentId !== segmentIdDirection)
                return;
            this.tripsFilterStore.set(tripsPerLayer);
            this.locationsFilterStore.set([...new Set(locations)]);
            this.selectedSegmentStore.set({ forward: forward, data: segmentData });
            this.selectedLocationStore.set(undefined);
            this.modelViewMapManager.zoomToLineString(segmentData.lineString, false);
            this.canCreateProjectStore.set(true);
        });
    }
    getModelDetails() {
        // cancel previous requests if any.
        if (typeof this.abortController !== "undefined") {
            this.abortController.abort();
            this.abortController = undefined;
        }
        this.abortController = new AbortController();
        const signal = this.abortController.signal;
        // get model data and setup layers.
        appManager.api.trips.getModel({
            modelGlobalId: get(this.modelGlobalIdStore),
            signal: signal
        }).then(modelData => {
            var _a;
            const layers = [];
            let hasNonCyclingLayer = false;
            this.modelDataStore.set(modelData);
            modelData.layers.forEach(l => {
                if (l.vehicleType != 2)
                    hasNonCyclingLayer = true;
                layers.push({
                    layer: l,
                    vehicle: appManager.api.trips.vehicles[l.vehicleType],
                    visible: writable(l.visible),
                    enabled: derived(this.showCyclingScoreStore, (value) => {
                        if (!value)
                            return true;
                        return l.vehicleType === 2;
                    }),
                });
            });
            this.modelTranslationKeyStore.set(modelData.properties.localization_key);
            this.layersAndStatusStore.set(layers);
            this.showCyclingScoreStore.set(!hasNonCyclingLayer);
            const showLocations = modelData.properties.show_locations !== "false";
            this.showRelevantLocationsToggleStore.set(showLocations);
            this.showRelevantLocationsStore.set(showLocations);
            const minZoom = Number((_a = modelData.properties) === null || _a === void 0 ? void 0 : _a.minzoom);
            if (!isNaN(minZoom)) {
                this.modelViewMapManager.setMinZoom(minZoom);
            }
            else {
                this.modelViewMapManager.setMinZoom(0);
            }
            if (typeof modelData.extent !== "undefined" &&
                modelData.extent != null) {
                this.modelViewMapManager.zoomToExtent(modelData.extent);
            }
        });
    }
    createProject() {
        this.createProjectOrSigninAsync().then();
    }
    async createProjectOrSigninAsync() {
        const signedIn = await appManager.isSignedIn();
        if (!signedIn) {
            await appManager.startSignin(window.location.href);
        }
        else {
            await this.createProjectAsync();
        }
    }
    async createProjectAsync() {
        var _a, _b;
        const selection = get(this.selectionStore);
        const locationIds = [];
        if (typeof selection.locationId !== "undefined") {
            locationIds.push(selection.locationId);
        }
        else {
            locationIds.push(...this.modelViewMapManager.getLocationsInView());
        }
        this.alertStateStore.set({ messageKey: "alert.creatingproject", open: true, icon: "thumbs-up" });
        // get location details.
        const locationsData = [];
        for (const l of locationIds) {
            locationsData.push(await appManager.api.trips.getLocation(l));
        }
        try {
            // get the user organizations.
            const organizations = await appManager.api.project.getUserOrganizations({
                accessToken: await appManager.api.getAccessToken(),
            });
            // open the new project modal, user has multiple organizations.
            if (organizations.length == 0) {
                throw new Error("User without an organization");
            }
            let organization = organizations[0];
            if (organizations.length > 1) {
                // for now we assume ANYWAYS admin users are the only ones that can have multiple organizations.
                organization = organizations.find(x => x.id == "787c5035-abd6-4e45-9cf3-f2b4c6154493");
                if (typeof organization === "undefined") {
                    organization = organizations.find(x => x.name == "ANYWAYS");
                }
                if (typeof organization === "undefined") {
                    throw new Error("Cannot determine user organization");
                }
            }
            // create a project, a branch and add matrix.
            const bounds = this.modelViewMapManager.map.getBounds();
            const polygon = turf.polygon([
                [
                    [bounds.getWest(), bounds.getNorth()],
                    [bounds.getEast(), bounds.getNorth()],
                    [bounds.getEast(), bounds.getSouth()],
                    [bounds.getWest(), bounds.getSouth()],
                    [bounds.getWest(), bounds.getNorth()],
                ],
            ]);
            const accessToken = await appManager.api.getAccessToken();
            const commits = await appManager.api.edit.getLatestSnapshotCommits({
                accessToken: await appManager.api.getAccessToken(),
            });
            const planet = commits.find((x) => x.snapshot.name === "planet");
            this.alertStateStore.set({ messageKey: "alert.creatingscenarios", open: true, icon: "thumbs-up" });
            const scenario0Branch = await appManager.api.edit.createBranch({
                accessToken,
                extentPolygon: polygon,
                snapshotCommitId: planet.id,
            });
            const scenario1Branch = await appManager.api.edit.copyBranch({
                accessToken: await appManager.api.getAccessToken(),
                branchId: scenario0Branch.id,
            });
            const newProject = await appManager.api.project.createProject({
                accessToken: accessToken,
                name: "Cycling to school",
                organizationId: organization.id,
                area: polygon.geometry,
                scenarios: [
                    {
                        branchId: scenario0Branch.id,
                        name: "Scenario 0",
                        description: "",
                        functionalName: "0",
                    },
                    {
                        branchId: scenario1Branch.id,
                        name: "Scenario 1",
                        description: "",
                        functionalName: "1",
                    }
                ],
            });
            const projectId = newProject.id;
            // create matrices.
            const matrices = [];
            this.alertStateStore.set({ messageKey: "alert.creatingmatrices", open: true, icon: "thumbs-up" });
            for (const locationData of locationsData) {
                const locations = [];
                const movements = [];
                locations.push({
                    latitude: locationData.latitude,
                    longitude: locationData.longitude,
                    type: OriginDestinationLocationType.PointOfInterest,
                });
                locationData.startingTrips.forEach((t => {
                    t.trips.forEach(trip => {
                        const targetId = locations.length;
                        locations.push({
                            latitude: trip.endLocation.latitude,
                            longitude: trip.endLocation.longitude,
                            type: OriginDestinationLocationType.Residential,
                        });
                        movements.push({
                            count: 1,
                            fromIdx: 0,
                            toIdx: targetId
                        });
                    });
                }));
                const name = (_b = (_a = locationData.tags) === null || _a === void 0 ? void 0 : _a.get("name")) !== null && _b !== void 0 ? _b : "Location Trips";
                const matrix = await appManager.api.project.createMatrix({
                    accessToken: accessToken,
                    matrix: {
                        name: name,
                        locations: locations,
                        movements: movements,
                        projectId: projectId,
                        description: "",
                        publicTags: [],
                        tags: {},
                        type: OriginDestinationMatrixType.OneToManyRandom
                    }
                });
                matrices.push({
                    id: Guid.create().toString(),
                    color: "#000",
                    matrixId: matrix.id,
                    visible: true,
                    layers: [{
                            color: "#0000FF",
                            profile: "bicycle.commute",
                            visible: true,
                            visualizationType: "bicycle.safety"
                        }]
                });
            }
            await appManager.api.project.createTripsLayer({
                accessToken: accessToken,
                tripsLayer: {
                    matrices: matrices,
                    name: "Trips from model",
                    projectId: projectId
                }
            });
            // create a default route planner
            await appManager.api.project.createReroute({
                accessToken,
                data: {
                    projectId: newProject.id,
                    name: "Default routeplanner",
                    published: false,
                    scenarios: [
                        newProject.scenarios[0].id,
                        newProject.scenarios[1].id
                    ]
                }
            });
            this.alertStateStore.set({ open: false });
            const scenario = newProject.scenarios[1].id;
            window.open(`https://www.anyways.eu/app/scenario/${scenario}`, "_blank");
        }
        catch (_c) {
            this.alertStateStore.set({ messageKey: "alert.creatingprojecterror", open: true, icon: "alert-triangle", autoClose: 2000, type: "danger" });
        }
    }
}
